import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, TextField, Typography, Box } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllContacts } from '../../../services/react-query/contacts';
import { useCreateContact } from '../../../services/react-query/contacts/useCreateContact';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { Contact } from '../../../types/Contact';
import CreateContactForm from '../../contacts/CreateContactForm';

interface IAddContactModalProps {
  accountUuid: string;
  open: boolean;
  alreadyAddedContacts: string[];
  onClose: () => void;
  onAdd: (contacts: Contact[]) => void;
}

const NEW_OPTION: Contact = {
  uuid: 'new',
  full_name: '➕ Create New Contact',
} as Contact;

export default function AddContactModal({ accountUuid, open, alreadyAddedContacts, onClose, onAdd }: IAddContactModalProps) {
  const { data: contactsData } = useGetAllContacts({ params: { filters: { account: accountUuid }, per_page: 0 } });

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [newContact, setNewContact] = useState<Contact>({ account_uuid: accountUuid } as Contact);

  // add the contact to the list and closing the modal
  const handleAdd = (contact: Contact) => {
    onAdd([contact]);
    onClose();
  };

  const { mutate: createContact, isPending } = useCreateContact({
    onSuccessCallback: (created) => {
      handleAdd(created);
    },
  });

  const selectableContacts = _.reject(contactsData?.data, (c: Contact) => _.includes(alreadyAddedContacts, c.uuid));

  const contactSelectionOptions = useMemo(() => {
    return [NEW_OPTION, ...(selectableContacts || [])];
  }, [selectableContacts]);

  useEffect(() => {
    if (!open) {
      setSelectedContact(null);
      setNewContact({ account_uuid: accountUuid } as Contact);
    }
  }, [open, accountUuid]);

  const isNew = selectedContact?.uuid === 'new';

  const handleSubmit = () => {
    if (!selectedContact) return;
    // If the contact is not new, add it to the list
    if (!isNew) {
      handleAdd(selectedContact);
      return;
    }
    // If the contact is new, create it and add it to the list on success
    createContact(newContact);
  };

  const handleCancel = () => {
    setSelectedContact(null);
    setNewContact({ account_uuid: accountUuid } as Contact);
    onClose();
  };

  const disabled = !selectedContact || (isNew && isPending);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogContentText>
        <Typography {...headerTypographyProps} sx={{ ml: 2, mt: 2 }}>
          Select Contact To Add
        </Typography>
        <Typography sx={{ ml: 2, mt: 1 }}>Please select the contact you would like to associate with this event.</Typography>
      </DialogContentText>

      <DialogContent>
        <Autocomplete
          options={contactSelectionOptions}
          value={selectedContact}
          getOptionLabel={(c: Contact) => c.full_name || ''}
          onChange={(_, value) => setSelectedContact(value)}
          renderInput={(params) => <TextField {...params} label="Select Contact" />}
        />

        {isNew && <CreateContactForm accountUuid={accountUuid} contact={newContact} setContact={setNewContact} />}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mx: 1 }}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>

        <Button variant="contained" color="primary" disabled={disabled} onClick={handleSubmit} loading={isPending}>
          {isNew ? 'Create & Add' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
